import { createSelector } from '@ngrx/store';
import { getAccountCocInSchedule } from '@reducers/account/account-service.selectors';
import { getPermissionState } from '@reducers/auth/permission.helper';
import { getDaylogsForPeriodAndDepartments } from '@reducers/orm/daylog/daylog.service';
import { getDepartmentEntities } from '@reducers/orm/department/department.service';
import { getEnhancedSchedules } from '@reducers/orm/schedule/schedule.service';
import { selectEntities as selectTimesheetConflictEntities } from '@reducers/orm/timesheet-conflict/timesheet-conflict.selectors';
import { type TimesheetModel } from '@reducers/orm/timesheet/timesheet.model';
import { getTimesheets } from '@reducers/orm/timesheet/timesheet.service';
import { departmentFilter } from '@reducers/selected-departments/selected-departments.service';
import { combinedFilter } from '@reducers/shared/entity.helper';
import { type PlainDate } from '@shiftbase-com/ts-shared/utilities';

import {
  applyCocSalaryOverride,
  enhanceTimesheetsWithLogAndPermissions,
  scheduleScopeFilters,
  timesheetScopeFilters,
} from '../shared/timesheet-helper.service';
import { type CategorizedGroupsInput } from './timesheet-list.selectors';

/**
 * Range-and-department-parameterised counterparts of the selectors in
 * `timesheet-helper.service.ts` and `timesheet-list.selectors.ts`, for callers (the payroll flow)
 * whose scope is explicit run state rather than the global `page-filters`/`selectedDepartments`
 * reducers those selectors read.
 *
 * The two chains share their predicates rather than restating them: `timesheetScopeFilters`,
 * `scheduleScopeFilters` and `applyCocSalaryOverride` are the same functions the global chain
 * uses, so the permission checks, CoC override and task exclusion cannot drift apart. The only
 * difference left is where the department set comes from — an argument here, the global
 * `selectedDepartments` there — which is the whole reason these exist.
 */
export const getTimesheetsForRangeAndDepartmentsWithCoc = (
  minDate: PlainDate,
  maxDate: PlainDate,
  departmentIds: string[],
) =>
  createSelector(
    getTimesheets,
    getPermissionState,
    getAccountCocInSchedule,
    (timesheets, permissionState, cocInScheduleActivated): TimesheetModel[] =>
      applyCocSalaryOverride(
        combinedFilter(
          timesheets,
          ...timesheetScopeFilters(minDate, maxDate, permissionState),
          departmentFilter(departmentIds),
        ),
        cocInScheduleActivated,
      ),
  );

export const getSchedulesForRangeAndDepartments = (minDate: PlainDate, maxDate: PlainDate, departmentIds: string[]) =>
  createSelector(getPermissionState, getEnhancedSchedules, (permissionState, schedules) =>
    combinedFilter(
      schedules,
      ...scheduleScopeFilters(minDate, maxDate, permissionState),
      departmentFilter(departmentIds),
    ),
  );

export const getEnhancedTimesheetsForRangeAndDepartments = (
  minDate: PlainDate,
  maxDate: PlainDate,
  departmentIds: string[],
) =>
  createSelector(
    getTimesheetsForRangeAndDepartmentsWithCoc(minDate, maxDate, departmentIds),
    getDaylogsForPeriodAndDepartments(departmentIds, minDate, maxDate),
    getPermissionState,
    (timesheets, daylogs, permissionState) =>
      enhanceTimesheetsWithLogAndPermissions(timesheets, daylogs, permissionState),
  );

/**
 * Range-and-department-parameterised counterpart of `getCategorizedGroupsInput`. The grouping
 * itself is left to the caller, which owns the priority map the API path supplies.
 */
export const getCategorizedGroupsInputForRange = (minDate: PlainDate, maxDate: PlainDate, departmentIds: string[]) =>
  createSelector(
    getEnhancedTimesheetsForRangeAndDepartments(minDate, maxDate, departmentIds),
    getSchedulesForRangeAndDepartments(minDate, maxDate, departmentIds),
    getDepartmentEntities,
    selectTimesheetConflictEntities,
    (timesheets, schedules, departmentsById, conflictEntities): CategorizedGroupsInput => ({
      timesheets,
      schedules,
      departmentsById,
      conflictEntities,
    }),
  );
